import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { AuthActions } from './auth.actions';
import { LoginService } from '../../services/login.service';
import { UsersService } from '../../services/users/users.service';
import { TokenStorage } from '../../core/auth/token.storage';

export const initAuth$ = createEffect(
    (actions$ = inject(Actions), tokenStorage = inject(TokenStorage)) =>
        actions$.pipe(
            ofType(AuthActions.initAuth),
            switchMap(() => {
                const token = tokenStorage.getToken();
                const user  = tokenStorage.getUser<any>();
                if (token && user) {
                    return of(AuthActions.restoreSession({ token, user }));
                }
                return EMPTY;
            })
        ),
    { functional: true }
);

export const login$ = createEffect(
    (actions$ = inject(Actions), loginService = inject(LoginService), tokenStorage = inject(TokenStorage)) =>
        actions$.pipe(
            ofType(AuthActions.login),
            switchMap(({ email, password }) =>
                loginService.login({ email, password }).pipe(
                    map((res: any) => {
                        tokenStorage.setToken(res.data?.token ?? res.token);
                        tokenStorage.setUser(res.data?.user ?? res.user);
                        loginService.isLoginSubject.next(true);
                        return AuthActions.loginSuccess({
                            token: res.data?.token ?? res.token,
                            user: res.data?.user ?? res.user
                        });
                    }),
                    catchError(err => of(AuthActions.loginFailure({
                        error: err?.error?.message ?? 'Login failed'
                    })))
                )
            )
        ),
    { functional: true }
);

export const loginSuccess$ = createEffect(
    (actions$ = inject(Actions), router = inject(Router)) =>
        actions$.pipe(
            ofType(AuthActions.loginSuccess),
            tap(() => router.navigate(['/admin-dashboard']))
        ),
    { functional: true, dispatch: false }
);

export const logout$ = createEffect(
    (actions$ = inject(Actions), loginService = inject(LoginService), router = inject(Router)) =>
        actions$.pipe(
            ofType(AuthActions.logout, AuthActions.sessionExpired),
            tap(() => {
                loginService.logout();
                router.navigate(['/home/login']);
            })
        ),
    { functional: true, dispatch: false }
);

export const loadPermissions$ = createEffect(
    (actions$ = inject(Actions), usersService = inject(UsersService)) =>
        actions$.pipe(
            ofType(AuthActions.loadPermissions),
            switchMap(() =>
                usersService.getUsersPermissions().pipe(
                    map((res: any) => AuthActions.loadPermissionsSuccess({
                        permissions: res.data ?? res
                    })),
                    catchError(err => of(AuthActions.loadPermissionsFailure({
                        error: err?.message ?? 'Failed to load permissions'
                    })))
                )
            )
        ),
    { functional: true }
);
